import type {
  Dealer,
  DealerContactWithConsent,
  DealerInteraction,
  DealerIsoPref,
  DealerResponseStats,
  DealerStatus,
  InteractionOutcome,
  InteractionType,
  PaintToSamplePref,
  PipelineStage,
  PreferredContactMethod,
  SessionUser,
} from '@motoveo/shared';
import {
  DEALER_STATUSES,
  INTERACTION_OUTCOMES,
  INTERACTION_TYPES,
  PAINT_TO_SAMPLE_PREFS,
  PIPELINE_STAGES,
  PREFERRED_CONTACT_METHODS,
} from '@motoveo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../api/client';
import { Layout } from '../components/Layout';
import { arrayToCsv, csvToArray, dollarsToCents, formatCents, formatDate } from '../lib/format';

function Field({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="field">
      <span className="field-label">{label}</span>
      <span className="field-value">{value ?? '—'}</span>
    </div>
  );
}

function consentPill(state: string): ReactNode {
  if (state === 'granted') return <span className="pill conf-high">granted</span>;
  if (state === 'revoked') return <span className="pill conf-low">revoked</span>;
  return <span className="pill">none</span>;
}

export function DealerDetailPage({ user }: { user: SessionUser }) {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ['dealer', id] });

  const detail = useQuery({ queryKey: ['dealer', id], queryFn: () => api.dealers.get(id), enabled: Boolean(id) });

  const setStatus = useMutation({
    mutationFn: (status: DealerStatus) => api.dealers.update(id, { status }),
    onSuccess: invalidate,
  });
  // refined model — franchise-only launch: only status='active' AND franchise=true dealers are blasted.
  const setFranchise = useMutation({
    mutationFn: (franchise: boolean) => api.dealers.update(id, { franchise }),
    onSuccess: invalidate,
  });

  return (
    <Layout user={user}>
      <p>
        <Link to="/dealers" className="muted">
          ← Dealers
        </Link>
      </p>

      {detail.isLoading ? (
        <p className="muted">Loading…</p>
      ) : detail.isError ? (
        <p className="error">{(detail.error as Error).message}</p>
      ) : detail.data ? (
        (() => {
          const { dealer: d, contacts, iso_prefs, interactions, response_stats } = detail.data;
          return (
            <>
              <div className="row-between">
                <h2>
                  {d.business_name} <span className="pill">{d.status}</span>{' '}
                  {d.franchise ? <span className="pill conf-high">franchise</span> : <span className="pill conf-low">non-franchise</span>}{' '}
                  <span className="pill conf-high">{d.pipeline_stage}</span>
                </h2>
                <div className="inline">
                  <label className="check" title="Only active FRANCHISE dealers receive blasts (franchise-only launch)">
                    <input
                      type="checkbox"
                      checked={d.franchise}
                      disabled={setFranchise.isPending}
                      onChange={(e) => setFranchise.mutate(e.target.checked)}
                    />{' '}
                    Porsche franchise store
                  </label>
                  <label className="inline">
                    Set status
                    <select value={d.status} disabled={setStatus.isPending} onChange={(e) => setStatus.mutate(e.target.value as DealerStatus)}>
                      {DEALER_STATUSES.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              </div>
              {setStatus.isError ? <p className="error">{(setStatus.error as Error).message}</p> : null}
              {setFranchise.isError ? <p className="error">{(setFranchise.error as Error).message}</p> : null}

              <PipelineCard dealer={d} onChange={invalidate} />

              <section className="grid-2">
                <div className="card">
                  <h3>Dealer</h3>
                  <Field label="State" value={d.state} />
                  <Field label="Subscription tier" value={d.subscription_tier} />
                  <Field label="Relationship owner" value={d.relationship_owner_name} />
                  <Field label="Preferred contact" value={d.preferred_contact_method} />
                  <Field label="Bid SLA (hrs)" value={d.response_sla_hours} />
                  <Field label="Notes" value={d.network_notes} />
                  <Field label="Reachable contacts" value={contacts.filter((c) => c.reachable).length} />
                  <Field label="Active ISO prefs" value={iso_prefs.filter((p) => p.active).length} />
                  <Field
                    label="Buyer-in-hand prefs"
                    value={iso_prefs.some((p) => p.active && p.has_named_buyer) ? <span className="pill conf-high">yes</span> : 'no'}
                  />
                </div>
                <ResponseStatsCard stats={response_stats} />
              </section>

              <LiveIsoCard dealerId={id} contacts={contacts} onChange={invalidate} />
              <ContactsCard dealerId={id} contacts={contacts} onChange={invalidate} />
              <IsoPrefsCard dealerId={id} prefs={iso_prefs} onChange={invalidate} />
              <InteractionsCard dealerId={id} interactions={interactions} contacts={contacts} onChange={invalidate} />
            </>
          );
        })()
      ) : null}
    </Layout>
  );
}

// --- contacts -------------------------------------------------------------

function ContactsCard({
  dealerId,
  contacts,
  onChange,
}: {
  dealerId: string;
  contacts: DealerContactWithConsent[];
  onChange: () => void;
}) {
  const capture = useMutation({
    mutationFn: (c: DealerContactWithConsent) =>
      api.dealers.captureConsent(dealerId, {
        contact_id: c.id,
        channel: c.phone_e164 ? 'sms' : 'email',
        consent_type: 'implied_business',
      }),
    onSuccess: onChange,
  });

  return (
    <div className="card">
      <h3>Contacts ({contacts.length})</h3>
      {capture.isError ? <p className="error">{(capture.error as Error).message}</p> : null}
      {contacts.length > 0 ? (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Phone</th>
                <th>Email</th>
                <th>Role</th>
                <th>Primary</th>
                <th>SMS consent</th>
                <th>Email consent</th>
                <th>Reachable</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {contacts.map((c) => (
                <tr key={c.id}>
                  <td>{c.name ?? '—'}</td>
                  <td className="muted">{c.phone_e164 ?? '—'}</td>
                  <td className="muted">{c.email ?? '—'}</td>
                  <td className="muted">{c.role_title ?? '—'}</td>
                  <td>{c.is_primary ? '●' : ''}</td>
                  <td>{consentPill(c.sms_consent)}</td>
                  <td>{consentPill(c.email_consent)}</td>
                  <td>{c.reachable ? <span className="ok-dot">●</span> : <span className="warn">—</span>}</td>
                  <td>
                    {!c.reachable ? (
                      <button className="plain" disabled={capture.isPending} onClick={() => capture.mutate(c)}>
                        Capture consent
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="muted">No contacts yet.</p>
      )}
      <AddContactForm dealerId={dealerId} onAdded={onChange} />
    </div>
  );
}

function AddContactForm({ dealerId, onAdded }: { dealerId: string; onAdded: () => void }) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [isPrimary, setIsPrimary] = useState(false);

  const add = useMutation({
    mutationFn: () =>
      api.dealers.addContact(dealerId, {
        name: name.trim() || undefined,
        phone_e164: phone.trim() || undefined,
        email: email.trim() || undefined,
        role_title: role.trim() || undefined,
        is_primary: isPrimary,
      }),
    onSuccess: () => {
      setName('');
      setPhone('');
      setEmail('');
      setRole('');
      setIsPrimary(false);
      onAdded();
    },
  });

  return (
    <form
      className="form"
      onSubmit={(e) => {
        e.preventDefault();
        add.mutate();
      }}
    >
      <h4>Add contact</h4>
      <div className="form-grid">
        <label>
          Name
          <input value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label>
          Phone (E.164)
          <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+13055551234" />
        </label>
        <label>
          Email
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </label>
        <label>
          Role
          <input value={role} onChange={(e) => setRole(e.target.value)} />
        </label>
      </div>
      <label className="check">
        <input type="checkbox" checked={isPrimary} onChange={(e) => setIsPrimary(e.target.checked)} /> Primary contact
      </label>
      <div className="row-between">
        <button type="submit" disabled={add.isPending || (!phone.trim() && !email.trim())}>
          {add.isPending ? 'Adding…' : 'Add contact'}
        </button>
        {add.isError ? <span className="error">{(add.error as Error).message}</span> : null}
      </div>
    </form>
  );
}

// --- ISO prefs ------------------------------------------------------------

function IsoPrefsCard({ dealerId, prefs, onChange }: { dealerId: string; prefs: DealerIsoPref[]; onChange: () => void }) {
  const toggle = useMutation({
    mutationFn: (p: DealerIsoPref) => api.dealers.updateIsoPref(p.id, { active: !p.active }),
    onSuccess: onChange,
  });

  return (
    <div className="card">
      <h3>In-search-of (ISO) prefs ({prefs.length})</h3>
      {toggle.isError ? <p className="error">{(toggle.error as Error).message}</p> : null}
      {prefs.length > 0 ? (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Label</th>
                <th>Models</th>
                <th>Gen</th>
                <th>Years</th>
                <th>Trans</th>
                <th>Miles ≤</th>
                <th>Budget ≤</th>
                <th>PTS</th>
                <th>Req codes</th>
                <th>Buyer</th>
                <th>Active</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {prefs.map((p) => (
                <tr key={p.id} className={p.active ? '' : 'muted'}>
                  <td>{p.label}</td>
                  <td>{arrayToCsv(p.models) || '—'}</td>
                  <td>{arrayToCsv(p.generations) || '—'}</td>
                  <td>{[p.year_min, p.year_max].some((y) => y != null) ? `${p.year_min ?? ''}–${p.year_max ?? ''}` : '—'}</td>
                  <td>{arrayToCsv(p.transmission) || 'any'}</td>
                  <td>{p.mileage_max ?? '—'}</td>
                  <td>{formatCents(p.budget_max_cents)}</td>
                  <td>{p.paint_to_sample}</td>
                  <td>{arrayToCsv(p.required_option_codes) || '—'}</td>
                  <td>{p.has_named_buyer ? <span className="pill conf-high">buyer</span> : '—'}</td>
                  <td>{p.active ? '●' : '—'}</td>
                  <td>
                    <button className="plain" disabled={toggle.isPending} onClick={() => toggle.mutate(p)}>
                      {p.active ? 'Deactivate' : 'Activate'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="muted">No ISO prefs yet — add one below.</p>
      )}
      <AddIsoPrefForm dealerId={dealerId} onAdded={onChange} />
    </div>
  );
}

function AddIsoPrefForm({ dealerId, onAdded }: { dealerId: string; onAdded: () => void }) {
  const [label, setLabel] = useState('');
  const [models, setModels] = useState('');
  const [generations, setGenerations] = useState('');
  const [yearMin, setYearMin] = useState('');
  const [yearMax, setYearMax] = useState('');
  const [manual, setManual] = useState(false);
  const [pdk, setPdk] = useState(false);
  const [mileageMax, setMileageMax] = useState('');
  const [budget, setBudget] = useState('');
  const [pts, setPts] = useState<PaintToSamplePref>('any');
  const [colors, setColors] = useState('');
  const [required, setRequired] = useState('');
  const [preferred, setPreferred] = useState('');
  const [namedBuyer, setNamedBuyer] = useState(false);

  const add = useMutation({
    mutationFn: () => {
      const transmission = [manual ? 'manual' : null, pdk ? 'pdk' : null].filter((t): t is 'manual' | 'pdk' => t != null);
      return api.dealers.addIsoPref(dealerId, {
        label: label.trim(),
        models: csvToArray(models),
        generations: csvToArray(generations),
        year_min: yearMin.trim() ? Number(yearMin) : undefined,
        year_max: yearMax.trim() ? Number(yearMax) : undefined,
        transmission,
        mileage_max: mileageMax.trim() ? Number(mileageMax) : undefined,
        budget_max_cents: dollarsToCents(budget) ?? undefined,
        exterior_colors: csvToArray(colors),
        paint_to_sample: pts,
        required_option_codes: csvToArray(required),
        preferred_option_codes: csvToArray(preferred),
        has_named_buyer: namedBuyer,
      });
    },
    onSuccess: () => {
      setLabel('');
      setModels('');
      setGenerations('');
      setYearMin('');
      setYearMax('');
      setManual(false);
      setPdk(false);
      setMileageMax('');
      setBudget('');
      setPts('any');
      setColors('');
      setRequired('');
      setPreferred('');
      setNamedBuyer(false);
      onAdded();
    },
  });

  return (
    <form
      className="form"
      onSubmit={(e) => {
        e.preventDefault();
        add.mutate();
      }}
    >
      <h4>Add ISO pref</h4>
      <div className="form-grid">
        <label>
          Label *
          <input value={label} onChange={(e) => setLabel(e.target.value)} required placeholder="PTS manual GT3s" />
        </label>
        <label>
          Models (comma-sep)
          <input value={models} onChange={(e) => setModels(e.target.value)} placeholder="992 GT3, 718 GT4" />
        </label>
        <label>
          Generations (comma-sep)
          <input value={generations} onChange={(e) => setGenerations(e.target.value)} placeholder="992, 991.2" />
        </label>
        <label>
          Year min
          <input inputMode="numeric" value={yearMin} onChange={(e) => setYearMin(e.target.value)} />
        </label>
        <label>
          Year max
          <input inputMode="numeric" value={yearMax} onChange={(e) => setYearMax(e.target.value)} />
        </label>
        <label>
          Mileage max
          <input inputMode="numeric" value={mileageMax} onChange={(e) => setMileageMax(e.target.value)} />
        </label>
        <label>
          Budget max ($)
          <input inputMode="decimal" value={budget} onChange={(e) => setBudget(e.target.value)} placeholder="300000" />
        </label>
        <label>
          Paint to sample
          <select value={pts} onChange={(e) => setPts(e.target.value as PaintToSamplePref)}>
            {PAINT_TO_SAMPLE_PREFS.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </select>
        </label>
        <label>
          Exterior colors (comma-sep; use PTS token)
          <input value={colors} onChange={(e) => setColors(e.target.value)} placeholder="Riviera Blue, PTS" />
        </label>
        <label>
          Required option codes (comma-sep)
          <input value={required} onChange={(e) => setRequired(e.target.value)} placeholder="450, 640" />
        </label>
        <label>
          Preferred option codes (comma-sep)
          <input value={preferred} onChange={(e) => setPreferred(e.target.value)} placeholder="2D2, P17" />
        </label>
      </div>
      <div className="checks">
        <label className="check">
          <input type="checkbox" checked={manual} onChange={(e) => setManual(e.target.checked)} /> Manual
        </label>
        <label className="check">
          <input type="checkbox" checked={pdk} onChange={(e) => setPdk(e.target.checked)} /> PDK
        </label>
        <label className="check consent">
          <input type="checkbox" checked={namedBuyer} onChange={(e) => setNamedBuyer(e.target.checked)} /> Has named buyer (the wedge —
          buyer-in-hand, can pay near-retail)
        </label>
      </div>
      <div className="row-between">
        <button type="submit" disabled={add.isPending || !label.trim()}>
          {add.isPending ? 'Adding…' : 'Add ISO pref'}
        </button>
        {add.isError ? <span className="error">{(add.error as Error).message}</span> : null}
      </div>
    </form>
  );
}

// --- M9: recruitment pipeline (stage + relationship + preferred contact + SLA) -------------

function PipelineCard({ dealer, onChange }: { dealer: Dealer; onChange: () => void }) {
  const [owner, setOwner] = useState(dealer.relationship_owner_name ?? '');
  const [method, setMethod] = useState<PreferredContactMethod | ''>(dealer.preferred_contact_method ?? '');
  const [sla, setSla] = useState(dealer.response_sla_hours != null ? String(dealer.response_sla_hours) : '');

  const setStage = useMutation({
    mutationFn: (pipeline_stage: PipelineStage) => api.dealers.update(dealer.id, { pipeline_stage }),
    onSuccess: onChange,
  });
  const save = useMutation({
    mutationFn: () =>
      api.dealers.update(dealer.id, {
        relationship_owner_name: owner.trim() || null,
        preferred_contact_method: method || null,
        response_sla_hours: sla.trim() ? Number(sla) : null,
      }),
    onSuccess: onChange,
  });

  return (
    <div className="card">
      <div className="row-between">
        <h3>Recruitment pipeline</h3>
        <label className="inline">
          Stage
          <select value={dealer.pipeline_stage} disabled={setStage.isPending} onChange={(e) => setStage.mutate(e.target.value as PipelineStage)}>
            {PIPELINE_STAGES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="form-grid">
        <label>
          Relationship owner (which founder)
          <input value={owner} onChange={(e) => setOwner(e.target.value)} placeholder="e.g. Alex" />
        </label>
        <label>
          Preferred contact
          <select value={method} onChange={(e) => setMethod(e.target.value as PreferredContactMethod | '')}>
            <option value="">—</option>
            {PREFERRED_CONTACT_METHODS.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </label>
        <label>
          Bid response SLA (hours)
          <input inputMode="numeric" value={sla} onChange={(e) => setSla(e.target.value)} placeholder="24" />
        </label>
      </div>
      <div className="row-between">
        <button type="button" disabled={save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : 'Save pipeline fields'}
        </button>
        {setStage.isError ? <span className="error">{(setStage.error as Error).message}</span> : null}
        {save.isError ? <span className="error">{(save.error as Error).message}</span> : null}
      </div>
    </div>
  );
}

// --- M9: response-rate history (derived from blasts + bids) --------------------------------

const pct = (x: number | null): string => (x == null ? '—' : `${Math.round(x * 100)}%`);

function ResponseStatsCard({ stats }: { stats: DealerResponseStats }) {
  return (
    <div className="card">
      <h3>Response history</h3>
      {stats.asked === 0 ? (
        <p className="muted">Not blasted yet — no response history.</p>
      ) : (
        <>
          <Field label="Asked (blast legs)" value={stats.asked} />
          <Field label="Reached (sent/delivered)" value={stats.reached} />
          <Field label="Responded" value={`${stats.responded} (${pct(stats.response_rate)})`} />
          <Field label="Bids" value={`${stats.bids} (${pct(stats.bid_rate)})`} />
          <Field label="No-bids" value={stats.no_bids} />
          <Field label="Avg turnaround" value={stats.avg_turnaround_hours == null ? '—' : `${stats.avg_turnaround_hours} h`} />
          <Field label="Last responded" value={formatDate(stats.last_responded_at)} />
        </>
      )}
    </div>
  );
}

// --- M9: LIVE-ISO capture (the <2-min post-call form; immediately matchable) ----------------

const CALL_SCRIPT_HINTS = [
  'Model/gen + trim (e.g. 992 GT3)',
  'Transmission (manual / PDK)',
  'Must-have option codes; deal-breaker colors',
  'Max mileage · buy budget',
  'Buyer-in-hand? (the wedge — can pay near-retail)',
];

function LiveIsoCard({ dealerId, contacts, onChange }: { dealerId: string; contacts: DealerContactWithConsent[]; onChange: () => void }) {
  const [label, setLabel] = useState('');
  const [models, setModels] = useState('');
  const [generations, setGenerations] = useState('');
  const [yearMin, setYearMin] = useState('');
  const [yearMax, setYearMax] = useState('');
  const [manual, setManual] = useState(false);
  const [pdk, setPdk] = useState(false);
  const [mileageMax, setMileageMax] = useState('');
  const [budget, setBudget] = useState('');
  const [pts, setPts] = useState<PaintToSamplePref>('any');
  const [colors, setColors] = useState('');
  const [required, setRequired] = useState('');
  const [preferred, setPreferred] = useState('');
  const [namedBuyer, setNamedBuyer] = useState(false);
  const [note, setNote] = useState('');
  const [contactId, setContactId] = useState('');
  const [lastResult, setLastResult] = useState<string | null>(null);

  const capture = useMutation({
    mutationFn: () => {
      const transmission = [manual ? 'manual' : null, pdk ? 'pdk' : null].filter((t): t is 'manual' | 'pdk' => t != null);
      return api.dealers.captureLiveIso(dealerId, {
        label: label.trim(),
        models: csvToArray(models),
        generations: csvToArray(generations),
        year_min: yearMin.trim() ? Number(yearMin) : undefined,
        year_max: yearMax.trim() ? Number(yearMax) : undefined,
        transmission,
        mileage_max: mileageMax.trim() ? Number(mileageMax) : undefined,
        budget_max_cents: dollarsToCents(budget) ?? undefined,
        exterior_colors: csvToArray(colors),
        paint_to_sample: pts,
        required_option_codes: csvToArray(required),
        preferred_option_codes: csvToArray(preferred),
        has_named_buyer: namedBuyer,
        interaction_note: note.trim() || undefined,
        dealer_contact_id: contactId || undefined,
      });
    },
    onSuccess: (res) => {
      setLastResult(`Captured — LIVE for matching now (${res.active_iso_pref_count} active ISO${res.active_iso_pref_count === 1 ? '' : 's'}; stage → ${res.pipeline_stage}).`);
      setLabel('');
      setModels('');
      setGenerations('');
      setYearMin('');
      setYearMax('');
      setManual(false);
      setPdk(false);
      setMileageMax('');
      setBudget('');
      setPts('any');
      setColors('');
      setRequired('');
      setPreferred('');
      setNamedBuyer(false);
      setNote('');
      setContactId('');
      onChange();
    },
  });

  return (
    <div className="card">
      <h3>⚡ Capture live ISO (on the call)</h3>
      <p className="muted">
        Capture the dealer's live buyer-demand in under 2 minutes. On save it is immediately usable by the matching engine, the dealer
        moves to <strong>live_iso</strong>, and the call is logged. Ask for:
      </p>
      <ul className="plain muted">
        {CALL_SCRIPT_HINTS.map((h) => (
          <li key={h}>• {h}</li>
        ))}
      </ul>
      <form
        className="form"
        onSubmit={(e) => {
          e.preventDefault();
          capture.mutate();
        }}
      >
        <div className="form-grid">
          <label>
            Label *
            <input value={label} onChange={(e) => setLabel(e.target.value)} required placeholder="PTS manual GT3 — buyer waiting" />
          </label>
          <label>
            Models (comma-sep)
            <input value={models} onChange={(e) => setModels(e.target.value)} placeholder="992 GT3, 718 GT4" />
          </label>
          <label>
            Generations (comma-sep)
            <input value={generations} onChange={(e) => setGenerations(e.target.value)} placeholder="992, 991.2" />
          </label>
          <label>
            Year min
            <input inputMode="numeric" value={yearMin} onChange={(e) => setYearMin(e.target.value)} />
          </label>
          <label>
            Year max
            <input inputMode="numeric" value={yearMax} onChange={(e) => setYearMax(e.target.value)} />
          </label>
          <label>
            Mileage max
            <input inputMode="numeric" value={mileageMax} onChange={(e) => setMileageMax(e.target.value)} />
          </label>
          <label>
            Budget max ($)
            <input inputMode="decimal" value={budget} onChange={(e) => setBudget(e.target.value)} placeholder="300000" />
          </label>
          <label>
            Paint to sample
            <select value={pts} onChange={(e) => setPts(e.target.value as PaintToSamplePref)}>
              {PAINT_TO_SAMPLE_PREFS.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </label>
          <label>
            Exterior colors (comma-sep; PTS token)
            <input value={colors} onChange={(e) => setColors(e.target.value)} placeholder="Riviera Blue, PTS" />
          </label>
          <label>
            Required option codes
            <input value={required} onChange={(e) => setRequired(e.target.value)} placeholder="450, 640" />
          </label>
          <label>
            Preferred option codes
            <input value={preferred} onChange={(e) => setPreferred(e.target.value)} placeholder="2D2, P17" />
          </label>
          <label>
            From contact
            <select value={contactId} onChange={(e) => setContactId(e.target.value)}>
              <option value="">—</option>
              {contacts.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name ?? c.phone_e164 ?? c.email ?? c.id.slice(0, 8)}
                </option>
              ))}
            </select>
          </label>
        </div>
        <label>
          Call note (logged with the capture)
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Has a cash buyer for a manual GT3, PTS a plus, up to 300k." />
        </label>
        <label className="check consent">
          <input type="checkbox" checked={namedBuyer} onChange={(e) => setNamedBuyer(e.target.checked)} /> Has named buyer (the wedge —
          buyer-in-hand, can pay near-retail)
        </label>
        <div className="row-between">
          <button type="submit" disabled={capture.isPending || !label.trim()}>
            {capture.isPending ? 'Capturing…' : '⚡ Capture live ISO'}
          </button>
          {capture.isError ? <span className="error">{(capture.error as Error).message}</span> : null}
          {lastResult ? <span className="ok-dot">{lastResult}</span> : null}
        </div>
      </form>
    </div>
  );
}

// --- M9: interaction log (every founder touch) + follow-up reminders ------------------------

function InteractionsCard({
  dealerId,
  interactions,
  contacts,
  onChange,
}: {
  dealerId: string;
  interactions: DealerInteraction[];
  contacts: DealerContactWithConsent[];
  onChange: () => void;
}) {
  const done = useMutation({
    mutationFn: (interactionId: string) => api.dealers.updateInteraction(interactionId, { follow_up_done: true }),
    onSuccess: onChange,
  });
  return (
    <div className="card">
      <h3>Interactions ({interactions.length})</h3>
      {done.isError ? <p className="error">{(done.error as Error).message}</p> : null}
      {interactions.length > 0 ? (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>When</th>
                <th>Type</th>
                <th>Outcome</th>
                <th>Notes</th>
                <th>Follow-up</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {interactions.map((it) => (
                <tr key={it.id}>
                  <td className="muted">{formatDate(it.occurred_at)}</td>
                  <td>
                    <span className="pill">{it.type}</span>
                  </td>
                  <td className="muted">{it.outcome ?? '—'}</td>
                  <td>{it.notes ?? '—'}</td>
                  <td className="muted">
                    {it.follow_up_at ? (
                      it.follow_up_done ? (
                        <span className="muted">done</span>
                      ) : (
                        <span className="warn">{formatDate(it.follow_up_at)}</span>
                      )
                    ) : (
                      '—'
                    )}
                    {it.follow_up_note ? ` — ${it.follow_up_note}` : ''}
                  </td>
                  <td>
                    {it.follow_up_at && !it.follow_up_done ? (
                      <button className="plain" disabled={done.isPending} onClick={() => done.mutate(it.id)}>
                        Mark done
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="muted">No interactions logged yet.</p>
      )}
      <LogInteractionForm dealerId={dealerId} contacts={contacts} onLogged={onChange} />
    </div>
  );
}

function LogInteractionForm({ dealerId, contacts, onLogged }: { dealerId: string; contacts: DealerContactWithConsent[]; onLogged: () => void }) {
  const [type, setType] = useState<InteractionType>('call');
  const [outcome, setOutcome] = useState<InteractionOutcome | ''>('');
  const [notes, setNotes] = useState('');
  const [contactId, setContactId] = useState('');
  const [followUp, setFollowUp] = useState('');
  const [followUpNote, setFollowUpNote] = useState('');
  const [advance, setAdvance] = useState<PipelineStage | ''>('');

  const log = useMutation({
    mutationFn: () =>
      api.dealers.logInteraction(dealerId, {
        type,
        outcome: outcome || undefined,
        notes: notes.trim() || undefined,
        dealer_contact_id: contactId || undefined,
        follow_up_at: followUp.trim() ? new Date(followUp).toISOString() : undefined,
        follow_up_note: followUpNote.trim() || undefined,
        advance_stage: advance || undefined,
      }),
    onSuccess: () => {
      setOutcome('');
      setNotes('');
      setContactId('');
      setFollowUp('');
      setFollowUpNote('');
      setAdvance('');
      onLogged();
    },
  });

  return (
    <form
      className="form"
      onSubmit={(e) => {
        e.preventDefault();
        log.mutate();
      }}
    >
      <h4>Log interaction</h4>
      <div className="form-grid">
        <label>
          Type
          <select value={type} onChange={(e) => setType(e.target.value as InteractionType)}>
            {INTERACTION_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </label>
        <label>
          Outcome
          <select value={outcome} onChange={(e) => setOutcome(e.target.value as InteractionOutcome | '')}>
            <option value="">—</option>
            {INTERACTION_OUTCOMES.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        </label>
        <label>
          Contact
          <select value={contactId} onChange={(e) => setContactId(e.target.value)}>
            <option value="">—</option>
            {contacts.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name ?? c.phone_e164 ?? c.email ?? c.id.slice(0, 8)}
              </option>
            ))}
          </select>
        </label>
        <label>
          Advance stage to
          <select value={advance} onChange={(e) => setAdvance(e.target.value as PipelineStage | '')}>
            <option value="">— (no change)</option>
            {PIPELINE_STAGES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
        <label>
          Follow-up at
          <input type="datetime-local" value={followUp} onChange={(e) => setFollowUp(e.target.value)} />
        </label>
        <label>
          Follow-up note
          <input value={followUpNote} onChange={(e) => setFollowUpNote(e.target.value)} placeholder="Call back re: GT3 buyer" />
        </label>
      </div>
      <label>
        Notes
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
      </label>
      <div className="row-between">
        <button type="submit" disabled={log.isPending}>
          {log.isPending ? 'Logging…' : 'Log interaction'}
        </button>
        {log.isError ? <span className="error">{(log.error as Error).message}</span> : null}
      </div>
    </form>
  );
}
